import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Modal, Textarea, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import NotificationsApi, {
  PushNotificationRecipient,
  PushSendSummary,
} from "../../Api/NotificationsApi";
import Loader from "../Common/Loader";

function displayName(r: PushNotificationRecipient): string {
  const name = [r.firstName, r.lastName].filter(Boolean).join(" ").trim();
  return name || r.emailAddress || `User ${r.userId}`;
}

const AdminPushNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [recipients, setRecipients] = useState<PushNotificationRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [title, setTitle] = useState("EMDA notification");
  const [body, setBody] = useState("You have a new notification from EMDA.");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState<PushSendSummary | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error("You do not have permission to access this page.");
      navigate("/");
    }
  }, [userModel, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await NotificationsApi.getPushRecipients(searchApplied);
      setRecipients(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users.");
      setRecipients([]);
    } finally {
      setLoading(false);
    }
  }, [searchApplied]);

  useEffect(() => {
    if (userModel?.userLevelId === UserLevelNames.SuperAdmin) {
      load();
    }
  }, [userModel, load]);

  const withTokenCount = useMemo(
    () => recipients.filter((r) => r.hasPushToken).length,
    [recipients]
  );

  const toggleRow = (userId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const selectAllWithToken = () => {
    setSelectedIds(
      new Set(recipients.filter((r) => r.hasPushToken).map((r) => r.userId))
    );
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectSelf = () => {
    if (userModel?.id) {
      setSelectedIds(new Set([userModel.id]));
    }
  };

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchApplied(search);
  };

  const openConfirm = () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one user.");
      return;
    }
    if (!title.trim() || !body.trim()) {
      toast.error("Title and message are required.");
      return;
    }
    setConfirmOpen(true);
  };

  const sendPush = async () => {
    try {
      setSending(true);
      const res = await NotificationsApi.sendPush(
        Array.from(selectedIds),
        title.trim(),
        body.trim()
      );
      setSummary(res.data);
      setSummaryOpen(true);
      setConfirmOpen(false);
      if (res.data.sent > 0) {
        toast.success(`Sent ${res.data.sent} push notification(s).`);
      } else {
        toast.warning("No notifications were sent. Check the results for details.");
      }
      await load();
    } catch (err) {
      console.error(err);
      toast.error("Failed to send push notifications.");
    } finally {
      setSending(false);
    }
  };

  if (!userModel || userModel.userLevelId !== UserLevelNames.SuperAdmin) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="w-full h-[74px] p-4">
        <div className="mx-1 flex items-end">
          <div className="flex flex-1">
            <h1 className="text-gray-900 font-bold text-4xl">Push Notifications</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-sm text-gray-600 max-w-3xl">
          Send a push notification to users who have registered a device token in the mobile app.
          Users without a token are shown but cannot receive notifications until they open the app
          on a physical device.
        </p>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <Textarea
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={500}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button color="blue" onClick={openConfirm} disabled={sending}>
                Send to selected ({selectedIds.size})
              </Button>
              <Button color="light" onClick={selectSelf}>
                Select me
              </Button>
            </div>
          </div>

          <form onSubmit={applySearch} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search users</label>
              <TextInput
                placeholder="Name, email, or company"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" color="light">Search</Button>
              <Button
                type="button"
                color="light"
                onClick={() => {
                  setSearch("");
                  setSearchApplied("");
                }}
              >
                Clear
              </Button>
              <Button type="button" color="light" onClick={selectAllWithToken}>
                Select all with token ({withTokenCount})
              </Button>
              <Button type="button" color="light" onClick={clearSelection}>
                Clear selection
              </Button>
            </div>
          </form>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-3 w-10"></th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Company</th>
                  <th className="p-3">Push token</th>
                </tr>
              </thead>
              <tbody>
                {recipients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-4 text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  recipients.map((r) => (
                    <tr key={r.userId} className="border-t border-gray-100">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedIds.has(r.userId)}
                          onChange={(e) => toggleRow(r.userId, e.target.checked)}
                        />
                      </td>
                      <td className="p-3">{displayName(r)}</td>
                      <td className="p-3">{r.emailAddress ?? "—"}</td>
                      <td className="p-3">{r.memberName ?? "—"}</td>
                      <td className="p-3">
                        {r.hasPushToken ? (
                          <span className="text-green-700">Registered</span>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Modal.Header>Send push notifications</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-700 mb-3">
            Send to <strong>{selectedIds.size}</strong> user(s)?
          </p>
          <div className="text-sm space-y-1">
            <p><strong>Title:</strong> {title}</p>
            <p><strong>Message:</strong> {body}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="blue" onClick={sendPush} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={summaryOpen} onClose={() => setSummaryOpen(false)} size="3xl">
        <Modal.Header>Send results</Modal.Header>
        <Modal.Body>
          {summary ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Attempted {summary.attempted}: {summary.sent} sent, {summary.skipped} skipped,
                {summary.errors} errors.
              </p>
              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-80">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="p-2">User</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.rows.map((row) => (
                      <tr key={row.userId} className="border-t border-gray-100">
                        <td className="p-2">{row.userId}</td>
                        <td className="p-2">{row.email ?? "—"}</td>
                        <td className="p-2">{row.status}</td>
                        <td className="p-2">{row.message ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <Loader />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setSummaryOpen(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminPushNotificationsPage;
